import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  CardHeader,
  CardContent,
  Grid,
  Box,
  CardActions,
  IconButton,
  Stack,
} from '@mui/material';
import JsFileDownloader from 'js-file-download';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  downloadExportedCsvReport,
  exportCsvFile,
  exportDistributorData,
  getExportedCsvList,
} from '../../services/import-export.service';
import ListImportExportData from '../form-elements/list-import-export-data.component';
import Iconify from '../common/iconify.component';
import { getCurrentTimestamp } from '../../utils/datetime.util';

const ListExport = () => {
  const [options, setOptions] = useState({
    rows: [],
    reloadCounter: 0,
    loading: true,
    error: false,
    downloadingFiles: [],
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [processing, setProccessing] = useState(false);
  const [downloadingDistributorsData, setDownloadingDistributorsData] =
    useState(false);

  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleDistributorExport = () => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    setDownloadingDistributorsData(true);

    exportDistributorData()
      .then((res) => {
        const currentTime = getCurrentTimestamp();
        const fileName = `distributors_${currentTime}.csv`;
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, fileName);
        setDownloadingDistributorsData(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setDownloadingDistributorsData(false);
      });
  };

  const handleExportFile = () => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    setProccessing(true);

    exportCsvFile()
      .then((res) => {
        if (res.data) {
          setProccessing(false);
          handleRefreshData();
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProccessing(false);
      });
  };

  const handleDownloadFile = (file) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    setOptions((prevOpt) => ({
      ...prevOpt,
      downloadingFiles: [...prevOpt.downloadingFiles, file.id],
    }));

    downloadExportedCsvReport(file.id)
      .then((res) => {
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, `${file.downloadFileName}`);
        setOptions((prevOpt) => ({
          ...prevOpt,
          downloadingFiles: prevOpt.downloadingFiles.filter(
            (df) => df !== file.id
          ),
        }));
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setOptions((prevOpt) => ({
          ...prevOpt,
          downloadingFiles: prevOpt.downloadingFiles.filter(
            (df) => df !== file.id
          ),
        }));
      });
  };

  useEffect(() => {
    getExportedCsvList()
      .then((res) => {
        setOptions({ ...options, loading: false, rows: res.data });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <Stack sx={{ py: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardHeader
              title="Export Database to CSV"
              subheader="Export current video database to CSV format for whitelabel provider. Select latest exports from options below."
              action={
                <IconButton
                  title="Refresh"
                  onClick={handleRefreshData}
                  disabled={options.loading || processing}
                >
                  <Iconify icon="ic:twotone-refresh" />
                </IconButton>
              }
            />

            <CardContent>
              <Box px={1}>
                <ListImportExportData
                  rows={options.rows}
                  loading={options.loading}
                  error={options.error}
                  handleDownloadFile={handleDownloadFile}
                  fromImport={false}
                  disabledDownload={processing}
                  downloadingFiles={options.downloadingFiles}
                />
              </Box>
            </CardContent>

            <CardContent
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                component="label"
                variant="contained"
                color="primary"
                disabled={options.loading || processing}
                onClick={() => handleExportFile()}
                startIcon={processing && <Iconify icon="eos-icons:loading" />}
              >
                Export
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardHeader
              title="Export Distributors Data"
              action={
                <Button
                  component="label"
                  variant="contained"
                  color="primary"
                  disabled={downloadingDistributorsData}
                  onClick={() => handleDistributorExport()}
                  startIcon={
                    downloadingDistributorsData && (
                      <Iconify icon="eos-icons:loading" />
                    )
                  }
                >
                  Download
                </Button>
              }
            />
            <CardActions />
          </Card>
        </Grid>
      </Grid>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Stack>
  );
};

export default ListExport;
